import { PvpFlag, flagStateOf } from "../zones/zones.ts";
import { creatureLevelRow } from "./creature-levels.ts";
import type { CreatureLevel, Level, Rules, XpGapRow } from "./types.ts";

/**
 * Levels and XP (ADR-083), pure and shared by the map server, which grants XP and raises levels, and the page, which
 * draws the XP bar from the same table its rules version names. The table is validated (`checkLevelsClimb`): level 1
 * at 0 XP, the XP strictly rising, so the rows are in order and the last is the cap.
 */

/**
 * Why XP was granted, as `progress` carries it (ADR-083 decision 6): a creature's fall, a PvP kill, a duel, or a
 * discovery, which a shrine is the first of (ADR-096's amendment of 2026-10-05).
 */
export const XpReason = { None: 0, Creature: 1, Pvp: 2, Duel: 3, Discovery: 4 } as const;
export type XpReason = (typeof XpReason)[keyof typeof XpReason];

/**
 * The highest reason this build numbers.
 */
export const LAST_XP_REASON = XpReason.Discovery;

const PERCENT = 100;
const PERCENT_SQUARED = PERCENT * PERCENT;

/**
 * The XP rates of a creature kill in whole percents (ADR-083's amendment of 2026-10-07, A3): Protected, Flagged or
 * Combatant, and anyone under a Hostile zone's lock. Plain data, read once from the rules' constants.
 */
export interface XpRates {
  readonly protected: number;
  readonly flagged: number;
  readonly hostile: number;
}

/**
 * Everything a creature kill's XP reads from the rules (ADR-083's note of 2026-10-15): the flag rates, the creature
 * level table, the level-gap table and the levels above a party mate who dealt no damage that a creature's level counts for. Plain data.
 */
export interface XpRules extends XpRates {
  readonly creatureLevels: readonly CreatureLevel[];
  readonly gaps: readonly XpGapRow[];
  readonly aboveCap: number;
  readonly partyShare: number;
  readonly partyBonus: number;
  readonly partyBonusMax: number;
  readonly partyTiles: number;
}

/**
 * The XP rules a set of rules holds: its rates, its `creature_levels` and `xp_gap` tables and its `xp_above_cap` and the party share's four numbers.
 *
 * @param rules The rules, or anything holding their tables and constants.
 * @returns The XP rules.
 */
export function xpRulesOf(rules: Pick<Rules, "constants" | "tables">): XpRules {
  return { ...xpRatesOf(rules), creatureLevels: rules.tables.creature_levels, gaps: rules.tables.xp_gap, aboveCap: rules.constants.xp_above_cap, partyShare: rules.constants.party_share_percent, partyBonus: rules.constants.party_bonus_percent, partyBonusMax: rules.constants.party_bonus_max_percent, partyTiles: rules.constants.party_share_tiles };
}

/**
 * The XP rates a set of rules holds: its `xp_percent_protected`, `xp_percent_flagged` and `xp_percent_hostile`.
 *
 * @param rules The rules, or anything holding their constants.
 * @returns The three rates in whole percents.
 * @example
 * xpRatesOf(loadRules());
 * // { protected: 100, flagged: 125, hostile: 150 }
 */
export function xpRatesOf(rules: Pick<Rules, "constants">): XpRates {
  const { constants } = rules;
  return { protected: constants.xp_percent_protected, flagged: constants.xp_percent_flagged, hostile: constants.xp_percent_hostile };
}

/**
 * The level a total of XP reaches: the last row whose XP the total meets, so a total past the last row's is the cap.
 *
 * @param levels The level table, validated.
 * @param xp The total XP, 0 or more.
 * @returns The level, 1 to the table's last.
 * @example
 * levelReached(loadRules().tables.levels, 450);
 * // 4
 */
export function levelReached(levels: readonly Level[], xp: number): number {
  let reached = 1;
  for (let i = 0; i < levels.length; i += 1) {
    const row = levels[i] as Level;
    if (row.xp > xp) {
      break;
    }
    reached = row.level;
  }
  return reached;
}

/**
 * A level's row, the level held to the table: below 1 (a thing's 0) reads as level 1 and past the cap as the last row.
 *
 * @param levels The level table, validated.
 * @param level The level.
 * @returns The row.
 */
export function levelRow(levels: readonly Level[], level: number): Level {
  const index = Math.min(levels.length, Math.max(1, level)) - 1;
  return levels[index] as Level;
}

/**
 * The level-gap modifier of a creature kill's XP (ADR-083 decision 3 as the author's roll-off of 2026-10-15 amends it),
 * from how far the earner stands above the creature: the last row of the table whose `minGap` the gap meets. A creature
 * at or above the earner is a gap of 0 or less, the table's first row.
 *
 * @param gaps The level-gap table, validated.
 * @param earnerLevel The earner's level.
 * @param creatureLevel The creature's level.
 * @returns Whole percents.
 * @example
 * gapXpPercent(loadRules().tables.xp_gap, 6, 1);
 * // 15
 */
export function gapXpPercent(gaps: readonly XpGapRow[], earnerLevel: number, creatureLevel: number): number {
  const gap = earnerLevel - creatureLevel;
  let percent = PERCENT;
  for (let i = 0; i < gaps.length; i += 1) {
    const row = gaps[i] as XpGapRow;
    if (row.minGap > gap) {
      break;
    }
    percent = row.percent;
  }
  return percent;
}

/**
 * The flag modifier of XP (Bible 4.3, ADR-083 decision 3 and its amendment of 2026-10-07) from an earner's flag byte:
 * Protected the rates' `protected`; under a Hostile zone's lock (the byte's `ZoneLocked` bit, Flagged or Combatant
 * alike) `hostile`; otherwise, Flagged or Combatant, `flagged`. The rates never stack, and the switching bit changes
 * nothing.
 *
 * @param flag The earner's flag byte.
 * @param rates The rules' XP rates.
 * @returns Whole percents.
 */
export function flagXpPercent(flag: number, rates: XpRates): number {
  if (flagStateOf(flag) === PvpFlag.Protected) {
    return rates.protected;
  }
  return (flag & PvpFlag.ZoneLocked) !== 0 ? rates.hostile : rates.flagged;
}

/**
 * The level a creature counts as for a party mate who dealt it no damage (ADR-083's amendment of 2026-10-21): at most
 * `aboveCap` levels above the mate, so a far higher creature's kill cannot jump a mate that took no part.
 *
 * @param earnerLevel The mate's level.
 * @param creatureLevel The creature's level.
 * @param xp The rules' XP rules.
 * @returns The level the mate's amount is read at.
 * @example
 * cappedCreatureLevel(5, 60, xpRulesOf(loadRules()));
 * // 7
 */
export function cappedCreatureLevel(earnerLevel: number, creatureLevel: number, xp: XpRules): number {
  return Math.min(creatureLevel, earnerLevel + xp.aboveCap);
}

/**
 * A creature kill's XP for one earner at the creature's level as handed in (ADR-083 and its amendment of 2026-10-21):
 * the template's XP is scaled by that level's `xpPercent` and floored, then `floor(xp * gapPercent * flagPercent / 10000)`
 * from the earner's gap to that level and its flag byte. A character that dealt damage passes the creature's real level;
 * a party mate who dealt none passes `cappedCreatureLevel`.
 *
 * @param templateXp The creature template's XP at level 1.
 * @param earnerLevel The earner's level.
 * @param creatureLevel The level the amount is read at.
 * @param flag The earner's flag byte at the kill tick.
 * @param xp The rules' XP rules.
 * @returns The XP, 0 or more.
 * @example
 * creatureKillXp(14, 6, 10, PvpFlag.Protected, xpRulesOf(loadRules()));
 * // 26, the level-10 amount
 */
export function creatureKillXp(templateXp: number, earnerLevel: number, creatureLevel: number, flag: number, xp: XpRules): number {
  const scaled = Math.floor((templateXp * creatureLevelRow(xp.creatureLevels, creatureLevel).xpPercent) / PERCENT);
  return Math.floor((scaled * gapXpPercent(xp.gaps, earnerLevel, creatureLevel) * flagXpPercent(flag, xp)) / PERCENT_SQUARED);
}

/**
 * A character's share of a kill's XP by its effort (ADR-083's note of 2026-10-16): its own full amount times its part
 * of the whole effort, with the party bonus taken from the exact share and the whole floored once. A bonus above 0 adds
 * at least 1 to a share of at least 1, so it never rounds away on a small share.
 *
 * @param fullXp What `creatureKillXp` gives the character alone.
 * @param effort The character's effort on the creature.
 * @param totalEffort The effort of everyone on the creature.
 * @param bonusPercent The party bonus in whole percents, 0 for none.
 * @returns The XP, 0 when there was no effort.
 */
export function effortXp(fullXp: number, effort: number, totalEffort: number, bonusPercent = 0): number {
  if (!(totalEffort > 0)) {
    return 0;
  }
  const share = Math.floor((fullXp * effort) / totalEffort);
  const bonused = Math.floor((fullXp * effort * (PERCENT + bonusPercent)) / (totalEffort * PERCENT));
  return bonusPercent > 0 && share >= 1 ? Math.max(bonused, share + 1) : bonused;
}

/**
 * The party bonus in whole percents (ADR-083's note of 2026-10-16): each other member nearby and not AFK adds
 * `partyBonus`, held to `partyBonusMax`.
 *
 * @param members How many other members are nearby and not AFK.
 * @param xp The rules' XP rules.
 * @returns The percent to add to the earner's share.
 */
export function partyBonusPercent(members: number, xp: XpRules): number {
  return Math.min(xp.partyBonusMax, members * xp.partyBonus);
}

/**
 * What a party member nearby who took no part earns: `partyShare` percent of its own full amount, floored.
 *
 * @param fullXp What `creatureKillXp` gives that member alone.
 * @param xp The rules' XP rules.
 * @returns The XP.
 */
export function partyShareXp(fullXp: number, xp: XpRules): number {
  return Math.floor((fullXp * xp.partyShare) / PERCENT);
}
